import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

//constants
import {
  PREPARE_TEXT,
  ANSWER_TEXT,
  QUESTION_PLACEHOLDER,
  UPLOAD_TEXT,
} from "../../video/constants/QuestionTemplateText";

//components
import Header from "../layout/Header";
import Timer from "../../video/components/Timer";
import Button from "../components/Button";
import VideoRecorder from "../../video/components/VideoRecorder";
import Alert from "../components/Alert";
import TechCheckModal from "../../video/modals/TechCheckModal";
import ConfirmationModal from "../../video/modals/ConfirmationModal";
import ClientBranding from "../components/ClientBranding";
//services
import { VideoRecordingManager } from "../../video/services/VideoRecorderManager";
import { questions } from "../../video/constants/Questions";
import { processAndUploadVideo } from "../../video/services/videoService";

const Question = () => {
  const { state } = useLocation();
  const employerPreferences = state?.employerPreferences || {
    // Default preferences for development
    retakeType: "UNLIMITED",
    retakeCount: 1,
    prepareTime: 0,
    answerTime: 10,
    prepareText: PREPARE_TEXT,
    answerText: ANSWER_TEXT,
    uploadText: UPLOAD_TEXT,
    questionPlaceholder: QUESTION_PLACEHOLDER,
  };

  const [questionState, setQuestionState] = useState({
    questionNumber: 1,
    mainText: employerPreferences.prepareText,
    cornerText: `Question 1 of ${Object.keys(questions).length}`,
    question: employerPreferences.questionPlaceholder,
    isPreparing: true,
    hasError: false,
    errorMessage: "",
    prepareTimer: employerPreferences.prepareTime,
    isPrepareTimerRunning: false,
    isRecordingComplete: false,
    videoBlob: null,
    isRefresh: false,
    showTechCheckModal: true,
    showConfirmationModal: false,
    retakesRemaining: employerPreferences.retakeCount,
    stream: null,
  });

  const [resetVideoRecorder, setResetVideoRecorder] = useState(null);
  const managerRef = useRef(new VideoRecordingManager());
  const navigate = useNavigate();
  // Memoize the reset handler to prevent unnecessary re-renders
  const handleResetVideoRecorder = useCallback((resetFn) => {
    setResetVideoRecorder(() => resetFn);
  }, []);

  // Update the recording state when recording starts/stops
  const handleRecordingBegin = useCallback(() => {
    setQuestionState((prev) => ({
      ...prev,
      isPreparing: false,
      mainText: employerPreferences.answerText,
    }));
  }, [employerPreferences.answerText]);

  const handleRecordingComplete = useCallback(
    (recordedVideo) => {
      setQuestionState((prev) => ({
        ...prev,
        mainText: employerPreferences.uploadText,
        isRecordingComplete: true,
        videoBlob: recordedVideo,
      }));
    },
    [employerPreferences.uploadText]
  );

  // Prepare Timer effect
  useEffect(() => {
    let intervalId;
    // Only run if retakeType is not UNLIMITED
    if (employerPreferences.retakeType !== "UNLIMITED") {
      if (
        questionState.prepareTimer > 0 &&
        questionState.isPrepareTimerRunning
      ) {
        intervalId = setInterval(() => {
          setQuestionState((prev) => ({
            ...prev,
            prepareTimer: prev.prepareTimer - 1,
          }));
        }, 1000);
      } else if (
        questionState.prepareTimer === 0 &&
        questionState.isPreparing
      ) {
        handleRecordingBegin();
      }
    }
    return () => clearInterval(intervalId);
  }, [
    questionState.prepareTimer,
    questionState.isPrepareTimerRunning,
    handleRecordingBegin,
    questionState.isPreparing,
    employerPreferences.retakeType,
  ]);

  // Initialize question
  useEffect(() => {
    if (
      !questionState.showTechCheckModal &&
      !questionState.showConfirmationModal
    ) {
      console.log("Initializing question");
      const initializeQuestion = setTimeout(() => {
        setQuestionState((prev) => ({
          ...prev,
          question: questions[`QUESTION_${prev.questionNumber}`],
          isPrepareTimerRunning: true,
        }));
      }, 1000);
      return () => clearTimeout(initializeQuestion);
    }
  }, [
    questionState.questionNumber,
    questionState.showTechCheckModal,
    questionState.showConfirmationModal,
    questionState.question,
  ]);

  // Handle errors during recording
  const handleRecordingError = (error) => {
    // Check if error is due to refresh during recording
    if (error.message === "Video data lost due to page refresh") {
      // Penalize the user by using up a retake
      const newRetakesRemaining = questionState.retakesRemaining - 1;

      setQuestionState((prev) => ({
        ...prev,
        hasError: true,
        errorMessage:
          "Video data lost due to page refresh. You have lost one retake attempt.",
        retakesRemaining: newRetakesRemaining,
        isRecordingComplete: false,
        videoBlob: null,
      }));

      // Delay the retry/submit action to allow the error to be seen
      setTimeout(() => {
        // If no retakes remaining, force move to next question
        if (newRetakesRemaining <= 0) {
          handleSubmit();
        } else {
          // Otherwise, allow them to retry
          handleRetry();
        }
      }, 3000); // Wait 3 seconds before proceeding
    } else {
      // Handle other errors normally
      setQuestionState((prev) => ({
        ...prev,
        hasError: true,
        errorMessage: error.message || "An error occurred during recording",
      }));
    }
  };

  // Handle tech check modal onClose
  const handleTechCheckModalClose = () => {
    setQuestionState((prev) => ({
      ...prev,
      showTechCheckModal: false,
      showConfirmationModal: true,
    }));
  };

  // Check for refresh on mount
  useEffect(() => {
    const hasCompletedTechCheck = sessionStorage.getItem(
      "hasCompletedTechCheck"
    );
    console.log("Checking refresh state...", hasCompletedTechCheck);

    const currentQuestion = sessionStorage.getItem("currentQuestion");

    if (hasCompletedTechCheck === "true" && currentQuestion) {
      setQuestionState((prev) => {
        const newState = {
          ...prev,
          isRefresh: true,
          showTechCheckModal: false,
          showConfirmationModal: true,
          questionNumber: parseInt(currentQuestion),
          cornerText: `Question ${currentQuestion} of ${Object.keys(questions).length}`,
          question: questions[`QUESTION_${currentQuestion}`],
        };
        return newState;
      });
    } else {
      console.log("Clearing session storage...");
      sessionStorage.clear();
    }
  }, []);

  // Save current question to session storage
  useEffect(() => {
    if (!questionState.showTechCheckModal) {
      sessionStorage.setItem(
        "currentQuestion",
        questionState.questionNumber.toString()
      );
      sessionStorage.setItem("hasCompletedTechCheck", "true");
    }
  }, [questionState.questionNumber, questionState.showTechCheckModal]);

  const handleConfirmationModalClose = async (action, stream) => {
    if (action === "confirm" && stream) {
      setQuestionState((prev) => ({
        ...prev,
        showConfirmationModal: false,
        stream: stream,
      }));

      try {
        await managerRef.current.initializeRecorderHelper();
      } catch (error) {
        console.error("Failed to initialize recorder:", error);
        setQuestionState((prev) => ({
          ...prev,
          hasError: true,
          errorMessage: "Failed to initialize recorder. Please try again.",
          isRefresh: false,
        }));
      }
    } else if (action === "cancel") {
      navigate("/basic-info");
    }
  };

  // Handle next question
  const handleRetry = useCallback(async () => {
    if (questionState.retakesRemaining === 0) {
      setQuestionState((prev) => ({
        ...prev,
        errorMessage: "No retakes remaining",
        hasError: true,
      }));
      return;
    }
    console.log("Retrying question");

    //Reset VideoRecorder
    if (resetVideoRecorder) {
      await resetVideoRecorder();
    }

    const newRetakesRemaining = questionState.retakesRemaining - 1;

    let newStreamObject = await managerRef.current.prepareNewRecording();

    if (employerPreferences.retakeType === "LIMIT_PER_INTERVIEW") {
      setQuestionState((prev) => ({
        ...prev,
        mainText: employerPreferences.answerText,
        hasError: false,
        errorMessage: "",
        isRecordingComplete: false,
        videoBlob: null,
        stream: newStreamObject.stream,
        retakesRemaining: newRetakesRemaining,
      }));
    } else if (employerPreferences.retakeType === "LIMIT_PER_QUESTION") {
      setQuestionState((prev) => ({
        ...prev,
        mainText: employerPreferences.prepareText,
        question: employerPreferences.questionPlaceholder,
        isPreparing: true,
        hasError: false,
        errorMessage: "",
        prepareTimer: employerPreferences.prepareTime,
        isPrepareTimerRunning: true,
        isRecordingComplete: false,
        videoBlob: null,
        stream: newStreamObject.stream,
        retakesRemaining: newRetakesRemaining,
      }));
    } else if (employerPreferences.retakeType === "UNLIMITED") {
      setQuestionState((prev) => ({
        ...prev,
        mainText: employerPreferences.prepareText,
        question: employerPreferences.questionPlaceholder,
        isPreparing: true,
        hasError: false,
        errorMessage: "",
        isPrepareTimerRunning: true,
        isRecordingComplete: false,
        videoBlob: null,
        stream: newStreamObject.stream,
      }));
    }
  }, [
    resetVideoRecorder,
    employerPreferences.answerText,
    employerPreferences.prepareTime,
    employerPreferences.prepareText,
    employerPreferences.retakeType,
    employerPreferences.questionPlaceholder,
    questionState.retakesRemaining,
  ]);

  // Handle next question
  const handleSubmit = useCallback(async () => {
    const nextQuestionNumber = questionState.questionNumber + 1;
    console.log("Retakes Remaining", questionState.retakesRemaining);

    // Upload video to server
    const response = await processAndUploadVideo(
      questionState.videoBlob,
      questionState.questionNumber
    );
    console.log("Upload video response", response);

    //Reset VideoRecorder
    if (resetVideoRecorder) {
      await resetVideoRecorder();
    }

    // If all questions are answered, navigate to results page
    if (nextQuestionNumber > Object.keys(questions).length) {
      // Handle completion of all questions
      console.log("All questions completed");

      //TODO: nav to results page
      navigate("/results");

      return;
    }

    let newStreamObject = await managerRef.current.prepareNewRecording();

    const newRetakesRemaining =
      employerPreferences.retakeType === "LIMIT_PER_QUESTION"
        ? employerPreferences.retakeCount
        : questionState.retakesRemaining;

    console.log("newRetakesRemaining", newRetakesRemaining);

    // Reset question state
    setQuestionState((prev) => ({
      ...prev,
      questionNumber: nextQuestionNumber,
      mainText: employerPreferences.prepareText,
      cornerText: `Question ${nextQuestionNumber} of ${Object.keys(questions).length}`,
      question: employerPreferences.questionPlaceholder,
      isPreparing: true,
      hasError: false,
      errorMessage: "",
      prepareTimer: employerPreferences.prepareTime,
      isPrepareTimerRunning: false,
      isRecordingComplete: false,
      videoBlob: null,
      retakesRemaining: newRetakesRemaining,
      stream: newStreamObject.stream,
    }));
  }, [
    resetVideoRecorder,
    questionState.questionNumber,
    employerPreferences.prepareTime,
    employerPreferences.prepareText,
    employerPreferences.retakeType,
    employerPreferences.retakeCount,
    employerPreferences.questionPlaceholder,
    questionState.retakesRemaining,
    questionState.videoBlob,
    navigate,
  ]);

  return (
    <div className="min-h-screen bg-gray-50">
      <TechCheckModal
        isOpen={questionState.showTechCheckModal}
        onClose={handleTechCheckModalClose}
        managerRef={managerRef}
      />
      <ConfirmationModal
        isOpen={questionState.showConfirmationModal}
        onClose={handleConfirmationModalClose}
        isRefresh={questionState.isRefresh}
        prepareTime={employerPreferences.prepareTime}
        answerTime={employerPreferences.answerTime}
        retakeType={employerPreferences.retakeType}
        retakeCount={employerPreferences.retakeCount}
        managerRef={managerRef}
      />
      <Header showLandingNav={false} />

      {/* Error Alert */}
      <Alert
        title="Recording Error"
        message={questionState.errorMessage}
        colorClass="error"
        showAlert={questionState.hasError}
        setShowAlert={() =>
          setQuestionState((prev) => ({ ...prev, hasError: false }))
        }
        duration={
          questionState.errorMessage.includes("page refresh") ? 15000 : 5000
        }
      />

      {/* Corner Text */}
      <div className="absolute top-4 right-4 text-gray-600">
        {questionState.cornerText}
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center justify-center">
          <div className="space-y-2">
            <ClientBranding
              logoUrl={employerPreferences?.logoUrl}
              companyName={employerPreferences?.companyName}
            />

            {/* Main Text */}
            <h3 className="text-xl italic text-gray-500 font-sans text-center">
              {questionState.mainText}
            </h3>
          </div>

          <div className="mt-8 space-y-8">
            {/* Timer - only show during prep time */}
            {employerPreferences.retakeType !== "UNLIMITED" &&
              questionState.isPreparing && (
                <div className="text-4xl font-bold">
                  <Timer seconds={questionState.prepareTimer} />
                </div>
              )}

            {/* Question */}
            <div className="text-2xl font-bold font-sans text-gray-800 text-center">
              {questionState.question}
            </div>

            {/* Video Recorder */}
            <VideoRecorder
              isPreparing={questionState.isPreparing}
              onReset={handleResetVideoRecorder}
              onError={handleRecordingError}
              answerTime={employerPreferences.answerTime}
              stream={questionState.stream}
              managerRef={managerRef}
              onRecordingBegin={handleRecordingBegin}
              onRecordingComplete={handleRecordingComplete}
            />

            {/* Only show submit button when ready */}
            {questionState.isRecordingComplete && (
              <div className="flex justify-center gap-4">
                {questionState.retakesRemaining > 0 && (
                  <Button
                    onClick={handleRetry}
                    className="mt-6"
                    text="Retake"
                  />
                )}
                <Button
                  onClick={handleSubmit}
                  className="mt-6"
                  text={
                    questionState.questionNumber ===
                    Object.keys(questions).length
                      ? "Finish"
                      : "Next Question"
                  }
                />
              </div>
            )}
            {employerPreferences.retakeType !== "UNLIMITED" && (
              <p className="text-sm text-gray-500">
                Retakes Remaining: {questionState.retakesRemaining}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Question;
